import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../providers/AuthContext";
import { getCompleteTariff } from "../../api/Tariffs";
import { buildTariffPriceRows } from "./TariffElementsOverview";
import { ENERGY_MODE, fromCompleteTariff, toElements } from "./wizard/tariffWizardState";

// Complete tariffs fetched on hover, so the next hover is instant. The list page clears it
// every time it loads the list, so the hover never shows prices older than the list.
const completeTariffCache = new Map();

function loadCompleteTariff(tariffPublicId, getAccessToken) {
    if (!completeTariffCache.has(tariffPublicId)) {
        const request = getAccessToken()
            .then((token) => getCompleteTariff(tariffPublicId, token))
            .catch((error) => {
                completeTariffCache.delete(tariffPublicId);
                throw error;
            });
        completeTariffCache.set(tariffPublicId, request);
    }
    return completeTariffCache.get(tariffPublicId);
}

export function clearTariffPriceCache() {
    completeTariffCache.clear();
}

const TariffPriceTooltip = ({ tariffPublicId, title }) => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const [state, setState] = useState({ loading: true, error: false, rows: [] });

    useEffect(() => {
        let cancelled = false;
        loadCompleteTariff(tariffPublicId, getAccessToken)
            .then((tariff) => {
                if (cancelled) {
                    return;
                }
                const wizardState = fromCompleteTariff(tariff);
                const spotEnergy =
                    wizardState.energyMode === ENERGY_MODE.SPOT ? wizardState.energy : null;
                const { energyRows, extraRows } = buildTariffPriceRows(
                    toElements(wizardState),
                    wizardState.basics.currency,
                    spotEnergy,
                    t
                );
                setState({ loading: false, error: false, rows: [...energyRows, ...extraRows] });
            })
            .catch(() => {
                if (!cancelled) {
                    setState({ loading: false, error: true, rows: [] });
                }
            });
        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per tariff; getAccessToken is not stable
    }, [tariffPublicId]);

    return (
        <div className="min-w-56">
            <div className="font-semibold mb-1">{title}</div>
            {state.loading && <div className="text-mute-500">{t("tariff.list.loadingPrices")}</div>}
            {state.error && <div className="text-error-600">{t("tariff.list.failedPrices")}</div>}
            {!state.loading && !state.error && state.rows.length === 0 && (
                <div className="text-mute-500">{t("tariff.preview.noPrices")}</div>
            )}
            {state.rows.map((row) => (
                <div key={row.key} className="border-t border-mute-200 py-1">
                    <div className="font-semibold">{row.label}</div>
                    {(row.blocks || []).map((block, index) => (
                        <div key={`${row.key}-${index}`} className="flex justify-between gap-4">
                            <span className="text-mute-600">{block.detail}</span>
                            <span className="tabular-nums whitespace-nowrap">
                                {block.inclValue || block.value || "—"}
                            </span>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

export default TariffPriceTooltip;
